import React from "react";
import moment from "moment";
import MatchSummaryCard from "../match-summary-card";
import { injectIntl, InjectedIntlProps } from "react-intl";
import messages from "./Matches.intl";
import { IMatch } from "../../types";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { getMatchResult } from "../molecules/cards";

interface IGroupFilterState {
  items: IMatch[];
  currentUser: {
    id: string;
    role: string;
  };
  emptyMessage?: string;
}

const TimeLineWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  backgroundColor: theme.palette.extra.backgroundGrey,
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(12), // Extra space to prevent navigation bar from covering last card
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const EmptyState = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(8, 2),
  margin: theme.spacing(2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "20px",
  border: `2px dashed ${theme.palette.extra.borderColor}`,
  color: theme.palette.text.secondary,
  fontSize: "1.1rem",
}));

/* Marcador de temporada — Played/Won/Drawn/Lost */
const StatsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.25),
}));

const StatTile = styled("div")(({ theme }: IStyledArguments) => ({
  flex: 1,
  textAlign: "center",
  background: "#FFFFFF",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 14,
  padding: theme.spacing(1.5, 0.25),
}));

const StatValue = styled("div")<{ $color?: string }>(
  ({ theme, $color }: IStyledArguments & { $color?: string }) => ({
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 800,
    fontSize: "1.2rem",
    color: $color || theme.palette.text.primary,
  })
);

const StatLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.6rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  color: theme.palette.text.secondary,
  marginTop: 2,
}));

/* Racha (últimos 5) + diferencia de goles acumulada */
const FormRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#FFFFFF",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 12,
  padding: theme.spacing(1.25, 1.75),
}));

const FormLabel = styled("span")(({ theme }: IStyledArguments) => ({
  fontSize: "0.7rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
}));

const FormPill = styled("span")<{ $result: "win" | "draw" | "loss" }>(
  ({ theme, $result }: IStyledArguments & { $result: "win" | "draw" | "loss" }) => ({
    width: 20,
    height: 20,
    borderRadius: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 4,
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 700,
    fontSize: "0.62rem",
    color: (theme.palette.extra as any)[$result],
    background: (theme.palette.extra as any)[`${$result}Soft`],
  })
);

const GoalDiffLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.6rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.3,
  color: theme.palette.text.secondary,
  textAlign: "right",
}));

const GoalDiffValue = styled("div")<{ $positive: boolean }>(
  ({ theme, $positive }: IStyledArguments & { $positive: boolean }) => ({
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 800,
    fontSize: "0.85rem",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    color: $positive ? theme.palette.extra.win : theme.palette.extra.loss,
  })
);

/* Ledger: próximo partido como entrada "pendiente", jugados agrupados por mes */
const Ledger = styled("div")(({ theme }: IStyledArguments) => ({
  background: "#FFFFFF",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 16,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
}));

const MonthDivider = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(1.1, 2.25, 0.5),
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.secondary,
  background: theme.palette.extra.backgroundGrey,
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
}));

const isFinished = (match: IMatch) => match.status === "FINISHED";

const MatchesList = injectIntl<IGroupFilterState & InjectedIntlProps>(
  ({ items, currentUser, intl, emptyMessage }) => {
    if (!items || !items.length) {
      return (
        <EmptyState>
          {emptyMessage || intl.formatMessage(messages.noMatches)}
        </EmptyState>
      );
    }

    const settled = [...items]
      .filter(isFinished)
      .sort(
        (a, b) =>
          Number(b.date.replace(/-/g, "")) - Number(a.date.replace(/-/g, ""))
      );
    const pending = [...items]
      .filter((match) => !isFinished(match))
      .sort(
        (a, b) =>
          Number(a.date.replace(/-/g, "")) - Number(b.date.replace(/-/g, ""))
      );

    const played = settled.length;
    const won = settled.filter((m) => getMatchResult(m.score.home, m.score.away) === "win").length;
    const drawn = settled.filter((m) => getMatchResult(m.score.home, m.score.away) === "draw").length;
    const lost = settled.filter((m) => getMatchResult(m.score.home, m.score.away) === "loss").length;
    const goalDiff = settled.reduce((acc, m) => acc + (m.score.home - m.score.away), 0);
    const form = settled.slice(0, 5).reverse();

    // Agrupa los jugados (ya ordenados desc.) por mes consecutivo, como secciones de un extracto
    const monthGroups: { label: string; matches: IMatch[] }[] = [];
    settled.forEach((match) => {
      const label = moment(match.date).format("MMMM YYYY");
      const lastGroup = monthGroups[monthGroups.length - 1];
      if (lastGroup && lastGroup.label === label) {
        lastGroup.matches.push(match);
      } else {
        monthGroups.push({ label, matches: [match] });
      }
    });

    return (
      <TimeLineWrapper>
        {played > 0 && (
          <div>
            <StatsRow style={{ marginBottom: 10 }}>
              <StatTile>
                <StatValue>{played}</StatValue>
                <StatLabel>{intl.formatMessage(messages.played)}</StatLabel>
              </StatTile>
              <StatTile>
                <StatValue $color="#1B8A5A">{won}</StatValue>
                <StatLabel>{intl.formatMessage(messages.won)}</StatLabel>
              </StatTile>
              <StatTile>
                <StatValue $color="#9A7A2C">{drawn}</StatValue>
                <StatLabel>{intl.formatMessage(messages.drawn)}</StatLabel>
              </StatTile>
              <StatTile>
                <StatValue $color="#B94438">{lost}</StatValue>
                <StatLabel>{intl.formatMessage(messages.lost)}</StatLabel>
              </StatTile>
            </StatsRow>

            <FormRow>
              <div style={{ display: "flex", alignItems: "center" }}>
                <FormLabel>{intl.formatMessage(messages.form)}</FormLabel>
                {form.map((match) => {
                  const result = getMatchResult(match.score.home, match.score.away);
                  const letter = result === "win" ? "W" : result === "draw" ? "D" : "L";
                  return (
                    <FormPill key={match.id} $result={result}>
                      {letter}
                    </FormPill>
                  );
                })}
              </div>
              <div>
                <GoalDiffLabel>{intl.formatMessage(messages.goalDifference)}</GoalDiffLabel>
                <GoalDiffValue $positive={goalDiff >= 0}>
                  {goalDiff > 0 ? `+${goalDiff}` : goalDiff}
                </GoalDiffValue>
              </div>
            </FormRow>
          </div>
        )}

        <Ledger>
          {pending.map((match) => (
            <MatchSummaryCard
              key={match.id}
              id={match.id}
              match={match}
              currentUser={currentUser}
            />
          ))}

          {monthGroups.map((group) => (
            <React.Fragment key={group.label}>
              <MonthDivider>{group.label}</MonthDivider>
              {group.matches.map((match) => (
                <MatchSummaryCard
                  key={match.id}
                  id={match.id}
                  match={match}
                  currentUser={currentUser}
                />
              ))}
            </React.Fragment>
          ))}
        </Ledger>
      </TimeLineWrapper>
    );
  }
);

export default MatchesList;
